import {createDirectionalLight, type DirectionalLight} from "@/dungeons/lighting/DirectionalLight.ts";

/** Coordinates in cells of this level's calibrated grid. */
export type GridPoint = { x: number; y: number };
/** Pixel coordinates in the level's original map image. */
export type ImagePoint = { x: number; y: number };
/** Shared tabletop coordinates (PIXELS_PER_METER units per meter). */
export type WorldPoint = { x: number; y: number };

export type LevelGrid = {
    type: "SQUARE";
    metersPerCell: number;
};

export type MapRegistration = {
    pixelsPerCell: number;
    gridOffsetX: number;
    gridOffsetY: number;
    calibrated: boolean;
};

export type WallPath = { id: string; points: GridPoint[] };
export type Door = {
    id: string;
    start: GridPoint;
    end: GridPoint;
    initialState: "OPEN" | "CLOSED";
    secret: boolean;
    label?: string | null;
    notes?: string | null;
};
export type LevelTransition = {
    id: string;
    points: GridPoint[];
    kind: "STAIRS_UP" | "STAIRS_DOWN" | "LADDER" | "ELEVATOR" | "OTHER";
    label?: string | null;
    targetLevelId?: string | null;
    arrivalImagePosition?: ImagePoint | null;
};
export type Annotation = {
    id: string;
    position: GridPoint;
    kind: "ROOM" | "NOTE" | "SECRET" | "TRAP" | "OTHER";
    label?: string | null;
    title: string;
    text?: string | null;
};
export type LightAnimation = {effect: "FLICKER" | "PULSE"; strength: number; speed: number};

export type DungeonLight = {
    id: string;
    position: GridPoint;
    preset: "CANDLE" | "TORCH" | "CAMPFIRE" | "CUSTOM";
    rangeMeters: number;
    intensity: number;
    /** Independent tint strength; absent on lights saved before this setting existed. */
    colorStrength?: number;
    color: string;
    enabled: boolean;
    /** Missing/null means static, including previously saved lights. */
    animation?: LightAnimation | null;
};

export type LevelGeometryDocument = {
    schemaVersion: 1;
    grid: LevelGrid;
    registration: MapRegistration;
    walls: WallPath[];
    doors: Door[];
    transitions: LevelTransition[];
    annotations: Annotation[];
    lights: DungeonLight[];
    directionalLight: DirectionalLight;
};

export const createEmptyLevelGeometry = (): LevelGeometryDocument => ({
    schemaVersion: 1,
    grid: {type: "SQUARE", metersPerCell: 1.5},
    registration: {pixelsPerCell: 150, gridOffsetX: 0, gridOffsetY: 0, calibrated: false},
    walls: [],
    doors: [],
    transitions: [],
    annotations: [],
    lights: [],
    directionalLight: createDirectionalLight(),
});
